import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ru } from 'date-fns/locale';
import { YMaps, Map, Placemark } from 'react-yandex-maps';
import styles from './InspectionSection.module.css';

const renderCustomHeader = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div className="custom-calendar-header">
    <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
      <svg
        width="6"
        height="10"
        viewBox="0 0 6 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.87402 8.83301L0.707357 4.66634L4.87402 0.499675"
          stroke="#474747"
          stroke-linecap="round"
        />
      </svg>
    </button>
    <span>{monthDate.toLocaleString('ru', { month: 'long' })}</span>
    <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
      <svg
        width="6"
        height="10"
        viewBox="0 0 6 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0.5 8.83301L4.66667 4.66634L0.5 0.499675"
          stroke="#474747"
          stroke-linecap="round"
        />
      </svg>
    </button>
    <span>{monthDate.getFullYear()}</span>
  </div>
);

const InspectionSection = ({ openSection, setOpenSection, sectionKey, carCompleted, setInspectionCompleted, selectedStation, setSelectedStation, setInspectionComment, setSelectedDate, setSelectedTime, setSelectedTimeStamp, selectedCarType, stations, loadingStations }) => {
  const [mapState, setMapState] = useState({
    center: [59.9343, 30.3351],
    zoom: 10,
  });
  const [currentMode, setCurrentMode] = useState('collapsed');
  const [activeField, setActiveField] = useState(null);
  const [localSelectedDate, setLocalSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [values, setValues] = useState({
    point: null,
    date: '',
    time: null,
    comment: '',
  });
  const [times, setTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(10);
  const [stationPrices, setStationPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [mapKey, setMapKey] = useState(0);


  // Проверяем, что дата не раньше сегодняшнего дня и не позже чем через 15 дней
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 15);
  const selectedDateStart = localSelectedDate ? new Date(localSelectedDate) : null;
  if (selectedDateStart) {
    selectedDateStart.setHours(0, 0, 0, 0);
  }
  const isDateValid = selectedDateStart && selectedDateStart >= today && selectedDateStart <= maxDate;
  const isDateWarning = selectedDateStart && selectedDateStart > new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

  const isCompleted = values.point && values.date && values.time && isDateValid;

  const fetchTimes = async (stationId, date) => {
    setLoadingTimes(true);
    try {
      const response = await fetch(
        `${window.apiBaseUrl || 'https://togto.ru/api/'}freeTime.php?stationId=${stationId}&date=${date}&carTypeId=${selectedCarType?.id || 1}`
      );
      const data = await response.json();
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
      let timesArray = Array.isArray(parsedData)
        ? parsedData
        : Object.values(parsedData);

      // Если дата сегодняшняя, фильтруем времена, которые не раньше текущего времени
      const today = new Date();
      const selectedDate = new Date(date + 'T00:00:00');
      const isToday = selectedDate.toDateString() === today.toDateString();

      if (isToday) {
        const now = new Date();
        timesArray = timesArray.filter(time => {
          const timeDate = new Date(time.timeStamp);
          return timeDate > now;
        });
      }

      setTimes(timesArray);
    } catch (error) {
      console.error('Error fetching times:', error);
      setTimes([]);
    } finally {
      setLoadingTimes(false);
    }
  };

  useEffect(() => {
    // Stations are passed as props, no need to fetch here
  }, [selectedCarType]);

  useEffect(() => {
    // Reset selected point and related values when car type changes
    if (selectedCarType) {
      setValues((prev) => ({ ...prev, point: null, date: '', time: null }));
      setSelectedStation(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedTimeStamp(null);
      setInspectionCompleted(false);
    }
  }, [selectedCarType, setSelectedStation, setSelectedDate, setSelectedTime, setSelectedTimeStamp, setInspectionCompleted]);

  useEffect(() => {
    setInspectionCompleted(isCompleted);
  }, [isCompleted, setInspectionCompleted]);

  useEffect(() => {
    if (selectedStation && localSelectedDate) {
      const dateStr = `${localSelectedDate.getFullYear()}-${String(
        localSelectedDate.getMonth() + 1
      ).padStart(2, '0')}-${String(localSelectedDate.getDate()).padStart(2, '0')}`;
      fetchTimes(selectedStation.id, dateStr);
      setValues(prev => ({ ...prev, time: null }));
    }
  }, [selectedStation, localSelectedDate]);

  useEffect(() => {
    if (stations.length > 0) {
      const fetchPrices = async () => {
        setLoadingPrices(true);
        const prices = {};
        const promises = stations.map(async (station) => {
          try {
            const response = await fetch(`${window.apiBaseUrl || 'https://togto.ru/api/'}price.php?stationId=${station.id}&carTypeId=${selectedCarType?.id || 1}`);
            const text = await response.text();
            let data;
            try {
              const parsed = JSON.parse(text);
              if (typeof parsed === 'string') {
                data = JSON.parse(parsed);
              } else {
                data = parsed;
              }
            } catch (e) {
              data = {};
            }
            prices[station.id] = data['price'] || 0;
          } catch (error) {
            prices[station.id] = 0;
          }
        });
        await Promise.all(promises);
        setStationPrices(prices);
        setMapKey(prev => prev + 1);
        setLoadingPrices(false);
      };
      fetchPrices();
    }
  }, [stations, selectedCarType]);

  React.useEffect(() => {
    if (openSection !== sectionKey) {
      setCurrentMode('collapsed');
    } else if (openSection === sectionKey && currentMode === 'collapsed') {
      enterForm();

      // Check if station was selected from map
      if (window.selectedStationFromMap) {
        if (stations.length > 0) {
          const station = stations.find(s => s.id === window.selectedStationFromMap);

          if (station) {
            setValues((prev) => ({ ...prev, point: station.address }));
            setSelectedStation(station);
          }
        } else {
          // Try again in 1 second
          setTimeout(() => {
            if (window.selectedStationFromMap && stations.length > 0) {
              const station = stations.find(s => s.id === window.selectedStationFromMap);
              if (station) {
                setValues((prev) => ({ ...prev, point: station.address }));
                setSelectedStation(station);
              }
              window.selectedStationFromMap = null;
            }
          }, 1000);
        }

        // Clear the global variable
        window.selectedStationFromMap = null;
      }

      // Check if station was selected from station page (when clicking "Записаться" button on station details page)
      if (window.selectedStationFromPage) {
        if (stations.length > 0) {
          const station = stations.find(s => s.id === window.selectedStationFromPage);

          if (station) {
            setValues((prev) => ({ ...prev, point: station.address }));
            setSelectedStation(station);
          }
        } else {
          // Try again in 1 second
          setTimeout(() => {
            if (window.selectedStationFromPage && stations.length > 0) {
              const station = stations.find(s => s.id === window.selectedStationFromPage);
              if (station) {
                setValues((prev) => ({ ...prev, point: station.address }));
                setSelectedStation(station);
              }
              window.selectedStationFromPage = null;
            }
          }, 1000);
        }

        // Clear the global variable
        window.selectedStationFromPage = null;
      }
    }
  }, [openSection, sectionKey, currentMode, stations]);

  const bodyRef = useRef(null);
  const calendarRef = useRef(null);

  const animateBody = (targetStyles) => {
    if (!bodyRef.current) return;

    // eslint-disable-next-line no-unused-expressions
    bodyRef.current.offsetHeight; 
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.style.transition =
        'opacity 0.6s ease-in-out, transform 0.6s ease-in-out';
      Object.assign(bodyRef.current.style, targetStyles);
    });
  };

  const enterCollapsed = () => {
    setCurrentMode('collapsed');
    animateBody({ opacity: '0', transform: 'translateY(-20px)' });
    setTimeout(() => {
      if (bodyRef.current) bodyRef.current.style.display = 'none';
    }, 300);
  };

  const enterForm = () => {
    setCurrentMode('form');
    if (bodyRef.current) {
      bodyRef.current.style.display = 'flex';
      bodyRef.current.style.opacity = '0';
      bodyRef.current.style.transform = 'translateY(-20px)';
      animateBody({ opacity: '1', transform: 'translateY(0)' });
    }
  };

  const enterList = (field) => {
    if (field === 'time' && !isDateValid) return;
    setCurrentMode('list');
    setActiveField(field);
  };

  const handleChevronClick = () => {
    if (currentMode === 'form') {
      setOpenSection(null);
      enterCollapsed();
    } else {
      if (!carCompleted) return; 
      setOpenSection(sectionKey);
      enterForm();
    }
  };

  const handleBackClick = () => {
    if (currentMode === 'list') {
      enterForm();
    } else {
      enterCollapsed();
    }
  };

  const handleOptionClick = (field, value, text) => {
    if (field === 'point') {
      setValues((prev) => ({ ...prev, [field]: text }));
      setSelectedStation(value);
    } else if (field === 'time') {
      setValues((prev) => ({ ...prev, [field]: text }));
      setSelectedTime(value);
      setSelectedTimeStamp(value.timeStamp);
    } else {
      setValues((prev) => ({ ...prev, [field]: text }));
    }
    enterForm();
  };

  const zoomHandlerRef = useRef(null);

  useEffect(() => {
    zoomHandlerRef.current = (e) => {
      const newZoom = e.get('newZoom');
      const newCenter = e.get('newCenter');

      setCurrentZoom(newZoom);
      setMapState({ center: newCenter, zoom: newZoom });
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target) && showCalendar) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  return (
    <div
      className={`${styles.section} ${styles.sectionPrimary}`}
      data-inspection-section
    >
      <div className={styles.sectionInner}>
        <div className={styles.sectionHeader} onClick={handleChevronClick}>
          <div className={styles.titles}>
            <div className={styles.icon} data-inspection-icon>
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M28.125 1.875H24.8438V1.40625C24.8438 0.629531 24.2142 0 23.4375 0C22.6608 0 22.0312 0.629531 22.0312 1.40625V1.875H7.96875V1.40625C7.96875 0.629531 7.33922 0 6.5625 0C5.78578 0 5.15625 0.629531 5.15625 1.40625V1.875H1.875C0.838594 1.875 0 2.71359 0 3.75V28.125C0 29.1614 0.838594 30 1.875 30H28.125C29.1614 30 30 29.1614 30 28.125V3.75C30 2.71359 29.1614 1.875 28.125 1.875ZM8.4375 24.8438C8.4375 25.103 8.22797 25.3125 7.96875 25.3125H5.15625C4.89703 25.3125 4.6875 25.103 4.6875 24.8438V22.5C4.6875 22.2408 4.89703 22.0312 5.15625 22.0312H7.96875C8.22797 22.0312 8.4375 22.2408 8.4375 22.5V24.8438ZM8.4375 19.6875C8.4375 19.9467 8.22797 20.1562 7.96875 20.1562H5.15625C4.89703 20.1562 4.6875 19.9467 4.6875 19.6875V17.3438C4.6875 17.0845 4.89703 16.875 5.15625 16.875H7.96875C8.22797 16.875 8.4375 17.0845 8.4375 17.3438V19.6875ZM8.4375 14.5312C8.4375 14.7905 8.22797 15 7.96875 15H5.15625C4.89703 15 4.6875 14.7905 4.6875 14.5312V12.1875C4.6875 11.9283 4.89703 11.7188 5.15625 11.7188H7.96875C8.22797 11.7188 8.4375 11.9283 8.4375 12.1875V14.5312ZM14.0625 24.8438C14.0625 25.103 13.853 25.3125 13.5938 25.3125H10.7812C10.522 25.3125 10.3125 25.103 10.3125 24.8438V22.5C10.3125 22.2408 10.522 22.0312 10.7812 22.0312H13.5938C13.853 22.0312 14.0625 22.2408 14.0625 22.5V24.8438ZM14.0625 19.6875C14.0625 19.9467 13.853 20.1562 13.5938 20.1562H10.7812C10.522 20.1562 10.3125 19.9467 10.3125 19.6875V17.3438C10.3125 17.0845 10.522 16.875 10.7812 16.875H13.5938C13.853 16.875 14.0625 17.0845 14.0625 17.3438V19.6875ZM14.0625 14.5312C14.0625 14.7905 13.853 15 13.5938 15H10.7812C10.522 15 10.3125 14.7905 10.3125 14.5312V12.1875C10.3125 11.9283 10.522 11.7188 10.7812 11.7188H13.5938C13.853 11.7188 14.0625 11.9283 14.0625 12.1875V14.5312ZM19.6875 24.8438C19.6875 25.103 19.478 25.3125 19.2188 25.3125H16.4062C16.147 25.3125 15.9375 25.103 15.9375 24.8438V22.5C15.9375 22.2408 16.147 22.0312 16.4062 22.0312H19.2188C19.478 22.0312 19.6875 22.2408 19.6875 22.5V24.8438ZM19.6875 19.6875C19.6875 19.9467 19.478 20.1562 19.2188 20.1562H16.4062C16.147 20.1562 15.9375 19.9467 15.9375 19.6875V17.3438C15.9375 17.0845 16.147 16.875 16.4062 16.875H19.2188C19.478 16.875 19.6875 17.0845 19.6875 17.3438V19.6875ZM19.6875 14.5312C19.6875 14.7905 19.478 15 19.2188 15H16.4062C16.147 15 15.9375 14.7905 15.9375 14.5312V12.1875C15.9375 11.9283 16.147 11.7188 16.4062 11.7188H19.2188C19.478 11.7188 19.6875 11.9283 19.6875 12.1875V14.5312ZM25.3125 19.6875C25.3125 19.9467 25.103 20.1562 24.8438 20.1562H22.0312C21.772 20.1562 21.5625 19.9467 21.5625 19.6875V17.3438C21.5625 17.0845 21.772 16.875 22.0312 16.875H24.8438C25.103 16.875 25.3125 17.0845 25.3125 17.3438V19.6875ZM25.3125 14.5312C25.3125 14.7905 25.103 15 24.8438 15H22.0312C21.772 15 21.5625 14.7905 21.5625 14.5312V12.1875C21.5625 11.9283 21.772 11.7188 22.0312 11.7188H24.8438C25.103 11.7188 25.3125 11.9283 25.3125 12.1875V14.5312ZM29.0625 7.03125H0.9375V3.75C0.9375 3.2325 1.3575 2.8125 1.875 2.8125H5.15625V4.6875C5.15625 5.46422 5.78578 6.09375 6.5625 6.09375C7.33922 6.09375 7.96875 5.46422 7.96875 4.6875V2.8125H22.0312V4.6875C22.0312 5.46422 22.6608 6.09375 23.4375 6.09375C24.2142 6.09375 24.8438 5.46422 24.8438 4.6875V2.8125H28.125C28.6425 2.8125 29.0625 3.2325 29.0625 3.75V7.03125Z"
                  fill={isCompleted ? '#F92C2C' : '#D9D9D9'}
                />
              </svg>
            </div>
            <div className={styles.sectionName}>
              <div className={styles.sectionTitle}>Точка ТО</div>
              <div className={styles.sectionSubtitle}>
                Выберите удобное место
              </div>
            </div>
          </div>
          <div
            className={`${styles.chevron} ${
              currentMode === 'form' ? styles.chevronUp : styles.chevronDown
            }`}
          ></div>
        </div>

        <div
          className={styles.sectionBody}
          ref={bodyRef}
          style={{ display: currentMode === 'collapsed' ? 'none' : 'flex' }}
        >
          <div
            className={styles.form}
            style={{ display: currentMode === 'list' ? 'none' : 'flex' }}
          >
            <button
              className={`${styles.formRow} ${
                values.point ? styles.filled : ''
              }`}
              onClick={() => enterList('point')}
            >
              <span className={styles.formLabel}>
                {values.point || 'Пункт ТО'}
              </span>
              <div className={styles.formChevron}></div>
            </button>
              
            <div className={styles.dateTimeContainer}>
              <button
                  className={`${styles.formRow} ${
                    values.date ? styles.filled : ''
                  } ${values.date && !isDateValid ? styles.errorDate : ''}`}
                onClick={() => setShowCalendar(!showCalendar)}
              >
                <span className={styles.formLabel}>
                  {values.date || 'Дата'}
                </span>
                <div className={styles.calendarIcon}>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M28.125 1.875H24.8438V1.40625C24.8438 0.629531 24.2142 0 23.4375 0C22.6608 0 22.0312 0.629531 22.0312 1.40625V1.875H7.96875V1.40625C7.96875 0.629531 7.33922 0 6.5625 0C5.78578 0 5.15625 0.629531 5.15625 1.40625V1.875H1.875C0.838594 1.875 0 2.71359 0 3.75V28.125C0 29.1614 0.838594 30 1.875 30H28.125C29.1614 30 30 29.1614 30 28.125V3.75C30 2.71359 29.1614 1.875 28.125 1.875ZM8.4375 24.8438C8.4375 25.103 8.22797 25.3125 7.96875 25.3125H5.15625C4.89703 25.3125 4.6875 25.103 4.6875 24.8438V22.5C4.6875 22.2408 4.89703 22.0312 5.15625 22.0312H7.96875C8.22797 22.0312 8.4375 22.2408 8.4375 22.5V24.8438ZM8.4375 19.6875C8.4375 19.9467 8.22797 20.1562 7.96875 20.1562H5.15625C4.89703 20.1562 4.6875 19.9467 4.6875 19.6875V17.3438C4.6875 17.0845 4.89703 16.875 5.15625 16.875H7.96875C8.22797 16.875 8.4375 17.0845 8.4375 17.3438V19.6875ZM8.4375 14.5312C8.4375 14.7905 8.22797 15 7.96875 15H5.15625C4.89703 15 4.6875 14.7905 4.6875 14.5312V12.1875C4.6875 11.9283 4.89703 11.7188 5.15625 11.7188H7.96875C8.22797 11.7188 8.4375 11.9283 8.4375 12.1875V14.5312ZM14.0625 24.8438C14.0625 25.103 13.853 25.3125 13.5938 25.3125H10.7812C10.522 25.3125 10.3125 25.103 10.3125 24.8438V22.5C10.3125 22.2408 10.522 22.0312 10.7812 22.0312H13.5938C13.853 22.0312 14.0625 22.2408 14.0625 22.5V24.8438ZM14.0625 19.6875C14.0625 19.9467 13.853 20.1562 13.5938 20.1562H10.7812C10.522 20.1562 10.3125 19.9467 10.3125 19.6875V17.3438C10.3125 17.0845 10.522 16.875 10.7812 16.875H13.5938C13.853 16.875 14.0625 17.0845 14.0625 17.3438V19.6875ZM14.0625 14.5312C14.0625 14.7905 13.853 15 13.5938 15H10.7812C10.522 15 10.3125 14.7905 10.3125 14.5312V12.1875C10.3125 11.9283 10.522 11.7188 10.7812 11.7188H13.5938C13.853 11.7188 14.0625 11.9283 14.0625 12.1875V14.5312ZM19.6875 24.8438C19.6875 25.103 19.478 25.3125 19.2188 25.3125H16.4062C16.147 25.3125 15.9375 25.103 15.9375 24.8438V22.5C15.9375 22.2408 16.147 22.0312 16.4062 22.0312H19.2188C19.478 22.0312 19.6875 22.2408 19.6875 22.5V24.8438ZM19.6875 19.6875C19.6875 19.9467 19.478 20.1562 19.2188 20.1562H16.4062C16.147 20.1562 15.9375 19.9467 15.9375 19.6875V17.3438C15.9375 17.0845 16.147 16.875 16.4062 16.875H19.2188C19.478 16.875 19.6875 17.0845 19.6875 17.3438V19.6875ZM19.6875 14.5312C19.6875 14.7905 19.478 15 19.2188 15H16.4062C16.147 15 15.9375 14.7905 15.9375 14.5312V12.1875C15.9375 11.9283 16.147 11.7188 16.4062 11.7188H19.2188C19.478 11.7188 19.6875 11.9283 19.6875 12.1875V14.5312ZM25.3125 19.6875C25.3125 19.9467 25.103 20.1562 24.8438 20.1562H22.0312C21.772 20.1562 21.5625 19.9467 21.5625 19.6875V17.3438C21.5625 17.0845 21.772 16.875 22.0312 16.875H24.8438C25.103 16.875 25.3125 17.0845 25.3125 17.3438V19.6875ZM25.3125 14.5312C25.3125 14.7905 25.103 15 24.8438 15H22.0312C21.772 15 21.5625 14.7905 21.5625 14.5312V12.1875C21.5625 11.9283 21.772 11.7188 22.0312 11.7188H24.8438C25.103 11.7188 25.3125 11.9283 25.3125 12.1875V14.5312ZM29.0625 7.03125H0.9375V3.75C0.9375 3.2325 1.3575 2.8125 1.875 2.8125H5.15625V4.6875C5.15625 5.46422 5.78578 6.09375 6.5625 6.09375C7.33922 6.09375 7.96875 5.46422 7.96875 4.6875V2.8125H22.0312V4.6875C22.0312 5.46422 22.6608 6.09375 23.4375 6.09375C24.2142 6.09375 24.8438 5.46422 24.8438 4.6875V2.8125H28.125C28.6425 2.8125 29.0625 3.2325 29.0625 3.75V7.03125Z"
                      fill="#474747"
                    />
                  </svg>
                </div>
              </button>

              <button
                className={`${styles.formRow} ${
                  values.time ? styles.filled : ''
                }`}
                onClick={() => enterList('time')}
              >
                <span className={styles.formLabel}>
                  {values.time || 'Время'}
                </span>
                <div className={styles.formChevron}></div>
              </button>
            </div>
            {showCalendar && (
              <div className={styles.calendarPopup} ref={calendarRef}>
                <DatePicker
                  selected={localSelectedDate}
                  onChange={(date) => {
                    setLocalSelectedDate(date);
                    setSelectedDate(date);
                    setValues((prev) => ({
                      ...prev,
                      date: date.toLocaleDateString('ru-RU'),
                    }));
                    setShowCalendar(false);
                  }}
                  inline
                  locale={ru}
                  renderCustomHeader={renderCustomHeader}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)}
                />
              </div>
            )}

            <div className={styles.fieldPlate}>
              <div className={styles.fieldPlateInput}>
                <input
                  className={styles.fieldPlateControl}
                  type="text"
                  placeholder="Комментарий (необязательно)"
                  value={values.comment}
                  onChange={(e) => {
                    setValues((prev) => ({ ...prev, comment: e.target.value }));
                    setInspectionComment(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          {currentMode === 'list' && (
            <div className={styles.lists}>
              {activeField === 'point' && (
                <div className={styles.list}>
                  <div className={styles.searchContainer}>
                    <input
                      className={styles.listSearch}
                      type="text"
                      placeholder="Поиск пункта"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                      className={styles.mapButton}
                      onClick={() => {
                        setCurrentMode('list');
                        setActiveField('map');
                      }}
                    >
                      Карта
                    </button>
                  </div>
                  <ul className={styles.listOptions}>
                    {loadingStations || loadingPrices ? (
                      <li className={styles.listOption}>Загрузка...</li>
                    ) : (
                      stations
                        .filter((station) =>
                          station.address
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) &&
                          (!selectedCarType || loadingPrices || (stationPrices[station.id] > 0))
                        )
                        .sort((a, b) => a.address.localeCompare(b.address))
                        .map((station) => (
                          <li
                            key={station.id}
                            className={styles.listOption}
                            onClick={() =>
                              handleOptionClick('point', station, station.address)
                            }
                          >
                            {station.address}
                          </li>
                        ))
                    )}
                  </ul>
                </div>
              )}
              {activeField === 'date' && (
                <div className={styles.calendar}>
                <DatePicker
                  selected={localSelectedDate}
                  onChange={(date) => {
                    setLocalSelectedDate(date);
                    setSelectedDate(date);
                    setValues((prev) => ({
                      ...prev,
                      date: date.toLocaleDateString('ru-RU'),
                    }));
                    setShowCalendar(false);
                  }}
                  inline
                  locale={ru}
                  renderCustomHeader={renderCustomHeader}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 12 * 24 * 60 * 60 * 1000)}
                />
                </div>
              )}
              {activeField === 'time' && (
                <div className={styles.list}>
                  <ul className={styles.listOptions}>
                    {!isDateValid ? (
                      <li className={styles.listOption}>
                        нет записи на данную дату
                      </li>
                    ) : loadingTimes ? (
                      <li className={styles.listOption}>Загрузка...</li>
                    ) : (
                      (() => {
                        const filteredTimes = [...times].filter(
                          (time) =>
                            time.free === true ||
                            time.free === 'true' ||
                            time.free === 1 ||
                            time.free === '1'
                        );
                        if (filteredTimes.length === 0) {
                          return (
                            <li className={styles.listOption}>
                              нет записи на данную дату
                            </li>
                          );
                        }
                        return filteredTimes.map((time) => (
                          <li
                            key={time.timeStamp}
                            className={styles.listOption}
                            onClick={() =>
                              handleOptionClick('time', time, time.name)
                            }
                          >
                            {time.name}
                          </li>
                        ));
                      })()
                    )}
                  </ul>
                </div>
              )}
              {activeField === 'map' && (
                <div key="map-container">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: '16px',
                      paddingBlock: '16px',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <button
                      onClick={() => enterList('point')}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 18L9 12L15 6"
                          stroke="#474747"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span
                      style={{
                        fontSize: '18px',
                        fontWeight: '500',
                      }}
                    >
                      Выберите станцию ТО
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <YMaps
                      key="ymaps-instance"
                      query={{
                        apikey: '82e7cd24-c248-4b99-a925-5f2fc2b072f6',
                        lang: 'ru_RU',
                      }}
                    >
                      <Map
                        key={mapKey}
                        className={styles.mapContainer}
                        state={mapState}
                        style={{ width: '750px', height: '300px' }}
                        onBoundsChange={zoomHandlerRef.current}
                      >
                        {stations.map((station) => {
                            if (
                              !station.coordinates ||
                              station.coordinates.length < 2 ||
                              (selectedCarType && (stationPrices[station.id] || 0) <= 0)
                            ) {
                              return null;
                            }
                            return (
                              <Placemark
                                key={station.id}
                                geometry={[
                                  station.coordinates[1],
                                  station.coordinates[0],
                                ]}
                                properties={{
                                  hintContent: `${station.address} - ${stationPrices[station.id] || 0} р.`,
                                  balloonContent: `${station.address}\nЦена: ${stationPrices[station.id] || 0} р.`,
                                  iconCaption: `${stationPrices[station.id] || 0} р.`,
                                }}
                                options={{
                                  preset: 'islands#dotIcon',
                                  iconColor: '#F92C2C',
                                  iconContent: `${stationPrices[station.id] || 0}`,
                                  iconContentStyle: {
                                    color: '#FFFFFF',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                  },
                                }}
                                onClick={() =>
                                  handleOptionClick('point', station, station.address)
                                }
                              />
                            );
                          })}
                      </Map>
                    </YMaps>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentMode === 'form' && (
            <div className={styles.footer}>
              <button
                className={styles.buttonAccent}
                disabled={!isCompleted}
                onClick={() => {
                  enterCollapsed();
                  setOpenSection('contact');
                }}
              >
                Продолжить
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionSection;
